import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface ContaProvisionadaType {
    id: number;
    valor: number;
    taxa: number;
    dataInicial: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    recorrencia: number;
    contaFinanceiraId: number;
    contaFinanceiraName: string;
    categoriaFinanceiraId: number;
    categoriaFinanceiraName: string;
    formaPagamentoId: number;
    formaPagamentoName: string;
    tipoContaPagarId?: number;
    tipoContaPagarName?: string;
}

export interface CreateContaProvisionadaType {
    valor: number;
    taxa: number;
    dataInicial: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    empresaId: number;
    recorrencia: number;
    contaFinanceiraId: number;
    categoriaFinanceiraId: number;
    formaPagamentoId: number;
    tipoContaPagarId?: number;
}

export interface UpdateContaProvisionadaType {
    id: number;
    valor: number;
    taxa: number;
    dataInicial: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    recorrencia: number;
    contaFinanceiraId: number;
    contaFinanceiraName?: string;
    categoriaFinanceiraId: number;
    categoriaFinanceiraName?: string;
    formaPagamentoId: number;
    formaPagamentoName?: string;
    tipoContaPagarId?: number;
}

export default function useContaProvisionada() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listContaProvisionada, setListContaProvisionada] = useState<ContaProvisionadaType[]>([]);
    const [listContaProvisionadaByEmpresa, setListContaProvisionadaByEmpresa] = useState<ContaProvisionadaType[]>([]);
    const [contaProvisionadaPageCount, setContaProvisionadaPageCount] = useState<number>(0);
    const [initialListContaProvisionada, setInitialListContaProvisionada] = useState<ContaProvisionadaType[]>([]);
    const [contaProvisionada, setContaProvisionada] = useState<ContaProvisionadaType>({
        id: 0,
        valor: 0,
        taxa: 0,
        dataInicial: "",
        documento: "",
        codigoTransacao: 0,
        historico: "",
        bandeira: "",
        recorrencia: 0,
        contaFinanceiraId: 0,
        contaFinanceiraName: "",
        categoriaFinanceiraId: 0,
        categoriaFinanceiraName: "",
        formaPagamentoId: 0,
        formaPagamentoName: "",
        tipoContaPagarId: 0,
        tipoContaPagarName: "",
    });

    function createContaProvisionada(createContaProvisionadaType: CreateContaProvisionadaType): Promise<CreateContaProvisionadaType | null> {
        setLoading(true);
        return api
            .post(`/ContaProvisionada/create`, createContaProvisionadaType)
            .then((response) => {
                const createdContaProvisionada: CreateContaProvisionadaType = response.data;
                showToast("success", "Conta criada com sucesso.");
                setLoading(false);
                return createdContaProvisionada;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar conta provisionada. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar conta provisionada:", error);
                return null;
            });
    }

    function getAllContaProvisionadaPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
        dataInicial?: string,
    ): Promise<ContaProvisionadaType[] | null> {
        setLoading(true);
        let url = `/ContaProvisionada/getAllContaProvisionadaPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        if (dataInicial) {
            url += `&dataInicial=${dataInicial}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListContaProvisionada(response.data.result);
                setInitialListContaProvisionada(response.data.result);
                setContaProvisionadaPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar contas. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar contas provisionadas:", error);
                return null;
            });
    }

    function updateContaProvisionada(updateContaProvisionadaData: UpdateContaProvisionadaType): Promise<UpdateContaProvisionadaType | null> {
        setLoading(true);
        return api
            .put(`/ContaProvisionada/${updateContaProvisionadaData.id}`, updateContaProvisionadaData)
            .then((response) => {
                showToast("success", "Conta atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar conta:", error);
                return null;
            });
    }

    function deleteContaProvisionada(contaProvisionadaId: number): Promise<ContaProvisionadaType | null> {
        setLoading(true);
        return api
            .delete(`/ContaProvisionada/${contaProvisionadaId}`)
            .then(() => {
                setContaProvisionada({
                    id: 0,
                    valor: 0,
                    taxa: 0,
                    dataInicial: "",
                    documento: "",
                    codigoTransacao: 0,
                    historico: "",
                    bandeira: "",
                    recorrencia: 0,
                    contaFinanceiraId: 0,
                    contaFinanceiraName: "",
                    categoriaFinanceiraId: 0,
                    categoriaFinanceiraName: "",
                    formaPagamentoId: 0,
                    formaPagamentoName: "",
                    tipoContaPagarId: 0,
                    tipoContaPagarName: "",
                });
                showToast("success", "Conta deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar conta:", error);
                return null;
            });
    }

    function handleGetContaProvisionadaByEmpresaId(empresaId: number): Promise<ContaProvisionadaType[] | null> {
        setLoading(true);
        return api
            .get(`/ContaProvisionada/getContaProvisionadaByEmpresaId?empresaId=${empresaId}`)
            .then((response) => {
                const contaProvisionada: ContaProvisionadaType[] = response.data;
                setListContaProvisionadaByEmpresa(contaProvisionada);
                setLoading(false);
                return contaProvisionada;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações das contas provisionadas. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações das contas provisionadas:", error);
                return null;
            });
    }

    return {
        createContaProvisionada,
        getAllContaProvisionadaPaged,
        updateContaProvisionada,
        deleteContaProvisionada,
        setListContaProvisionada,
        listContaProvisionada,
        setContaProvisionadaPageCount,
        contaProvisionadaPageCount,
        setInitialListContaProvisionada,
        initialListContaProvisionada,
        setContaProvisionada,
        contaProvisionada,
        handleGetContaProvisionadaByEmpresaId,
        setListContaProvisionadaByEmpresa,
        listContaProvisionadaByEmpresa,
    };
}